import { isReleased, useCurrentUser, type Feature } from "@roastery/auth";
import { coversRoute, releaseEntries } from "./catalogue";

/**
 * The three questions the shell asks the catalogue: is this sidebar entry withheld, is this tab, and
 * is this address. All three are one lookup over the same table (EP-38, decision 5).
 */
export function useWithheld() {
  const { user, isLoading } = useCurrentUser();
  const withheld = releaseEntries.filter(([feature]) => !isReleased(user, feature as Feature));
  return {
    /**
     * Whether this address is inside *some* release entry while the answer is still on its way.
     *
     * <p>Found in verification: opening a withheld address cold — a bookmark, a link in an old
     * e-mail — renders the screen for the instant before `/me` arrives, and that instant is enough
     * for its queries to go out and come back 404. Guessing "withheld" instead would be worse: on a
     * developer's machine, where nothing is withheld, it would redirect away from a screen that is
     * perfectly available. So the screen waits rather than guesses.
     *
     * <p>"Still on its way" is `!user` and not only `isLoading`: a query that has not been enabled
     * yet is pending without loading (TanStack Query v5 counts `isLoading` as pending *and*
     * fetching), and that gap is exactly the moment the screen used to mount in.
     */
    pending: (pathname: string) =>
      (isLoading || !user) && releaseEntries.some(([, entry]) => coversRoute(entry, pathname)),
    /** A sidebar entry, by its `data-testid`. */
    nav: (testId: string) => withheld.some(([, entry]) => entry.nav?.includes(testId)),
    /** A tab, by the address it leads to. */
    tab: (to: string) => withheld.some(([, entry]) => entry.tabs?.includes(to)),
    /** An address somebody typed, followed a link to, or had bookmarked. */
    route: (pathname: string) => withheld.some(([, entry]) => coversRoute(entry, pathname)),
  };
}
